// src/context/AuthContext.js
// Firebase ilk boyamadan sonra tembel yüklenir; ana paket küçük kalır.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

const AuthContext = createContext({ user: null, userData: null, ready: false });

const SESSION_FLAG = 'oy-session';
const loadFirebase = () => Promise.all([import('../firebase'), import('firebase/auth'), import('../firebaseHelpers')]);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [ready, setReady] = useState(false);
  const [, setTick] = useState(0);
  const handleRef = useRef(() => {});

  useEffect(() => {
    let unsub = () => {};
    let cancelled = false;
    const start = () =>
      loadFirebase()
        .then(([fb, authMod, helpers]) => {
          if (cancelled) return;
          handleRef.current = async (u) => {
            setUser(u);
            setTick((t) => t + 1);
            try {
              if (u) localStorage.setItem(SESSION_FLAG, '1');
              else localStorage.removeItem(SESSION_FLAG);
            } catch {
              /* depolama kapalı */
            }
            if (u && !u.isAnonymous) {
              try {
                const data = await helpers.createOrUpdateUser(u);
                setUserData(data || (await helpers.getUserData(u.uid)));
              } catch (e) {
                console.error('Kullanıcı verisi alınamadı:', e);
              }
            } else {
              setUserData(null);
            }
            setReady(true);
            // Misafir: anonim oturum (Kristal cüzdanı ve online oyun için)
            if (!u) authMod.signInAnonymously(fb.auth).catch((e) => console.warn('Anonim giriş yapılamadı:', e?.code));
          };
          unsub = authMod.onAuthStateChanged(fb.auth, (u) => handleRef.current(u));
        })
        .catch((e) => {
          console.error('Firebase yüklenemedi:', e);
          setReady(true);
        });
    // Oturum anahtarı varsa hemen, yoksa tarayıcı boşta kalınca yükle
    let hasSession = false;
    try {
      hasSession = localStorage.getItem(SESSION_FLAG) === '1';
    } catch {
      hasSession = false;
    }
    let idle;
    if (hasSession) start();
    else if ('requestIdleCallback' in window) idle = window.requestIdleCallback(start, { timeout: 2500 });
    else idle = setTimeout(start, 1200);
    return () => {
      cancelled = true;
      unsub();
      if (idle) {
        if ('cancelIdleCallback' in window) window.cancelIdleCallback(idle);
        clearTimeout(idle);
      }
    };
  }, []);

  const login = useCallback(async () => {
    const [fb, authMod] = await loadFirebase();
    const cur = fb.auth.currentUser;
    try {
      if (cur && cur.isAnonymous) {
        // Misafir hesabı Google'a bağlanır: Kristal ve Oyuntu Puan korunur
        const res = await authMod.linkWithPopup(cur, fb.provider);
        await res.user.reload().catch(() => {});
        await handleRef.current(fb.auth.currentUser);
      } else {
        await authMod.signInWithPopup(fb.auth, fb.provider);
      }
    } catch (e) {
      if (e?.code === 'auth/popup-closed-by-user' || e?.code === 'auth/cancelled-popup-request') return;
      if (e?.code === 'auth/credential-already-in-use' || e?.code === 'auth/email-already-in-use') {
        // Bu Google hesabı daha önce kullanılmış: o hesaba geç
        const cred = authMod.GoogleAuthProvider.credentialFromError(e);
        if (cred) {
          await authMod.signInWithCredential(fb.auth, cred).catch(() => alert('Giriş yapılamadı. Lütfen tekrar deneyin.'));
          return;
        }
      }
      if (e?.code === 'auth/popup-blocked') {
        if (cur && cur.isAnonymous) await authMod.linkWithRedirect(cur, fb.provider);
        else await authMod.signInWithRedirect(fb.auth, fb.provider);
        return;
      }
      alert('Giriş yapılamadı. Lütfen tekrar deneyin.');
    }
  }, []);

  const logout = useCallback(async () => {
    const [fb, authMod] = await loadFirebase();
    await authMod.signOut(fb.auth);
  }, []);

  const refreshUserData = useCallback(async () => {
    if (!user) return;
    const [, , helpers] = await loadFirebase();
    setUserData(await helpers.getUserData(user.uid));
  }, [user]);

  const isAnon = !!(user && user.isAnonymous);
  const value = useMemo(
    () => ({ user: user && !isAnon ? user : null, anonUser: isAnon ? user : null, anyUser: user, uid: user ? user.uid : null, userData, ready, login, logout, refreshUserData, setUserData }),
    [user, isAnon, userData, ready, login, logout, refreshUserData]
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);

export const displayNameOf = (user, userData) => userData?.nickname || user?.displayName || 'Oyuncu';
